import { useLayoutEffect, useMemo, useRef, useState } from "react";
import classNames from "classnames";

import i18n from "@/localization/i18next";

const STICK_GAP = 2;
const STICK_WIDTH = 3;
const MIN_CONTAINER_WIDTH = 279;

const COLOR_THEMES = {
    red: ["bg-zing-yellow-200", "bg-zing-neon-700", "bg-zing-yellow-200", "bg-zing-orange-200"],
    blue: ["bg-zing-blue-50", "bg-zing-blue-100", "bg-zing-blue-200"],
};

type Range = {
    id: "underweight" | "normal" | "overweight" | "obese";
    min: number;
    max: number;
};

const getSticksRanges = (ranges: Range[], containerWidth: number) => {
    const stickCount = Math.floor(containerWidth / (STICK_WIDTH + STICK_GAP));
    const minMaxInterval = ranges[ranges.length - 1].max - ranges[0].min;
    const sticksInOneStep = stickCount / minMaxInterval;

    const sticksRanges = ranges.map(({ min, max }) => {
        const currentRangeDifference = max - min;
        const sticksRangeLength = Math.ceil(sticksInOneStep * currentRangeDifference);

        const step = currentRangeDifference / sticksRangeLength;
        return new Array(sticksRangeLength).fill(null).map((_, j) => min + j * step);
    });

    return sticksRanges;
};

const getValueRangeIndex = (value: number, ranges: Range[]) => {
    let valueRangeIndex = ranges.findIndex(({ max, min }) => value >= min && value < max);

    if (valueRangeIndex === -1) {
        const firstRange = ranges[0];
        const lastRange = ranges[ranges.length - 1];

        if (value < firstRange.min) {
            valueRangeIndex = 0;
        }
        if (value >= lastRange.max) {
            valueRangeIndex = ranges.length - 1;
        }
    }

    return valueRangeIndex;
};

const getValueStickPosition = (value: number, ranges: Range[], sticksRanges: number[][]) => {
    const valueRangeIndex = getValueRangeIndex(value, ranges);

    const activeRange = sticksRanges[valueRangeIndex];
    const closestStickValue = activeRange.reduce((prev, curr) =>
        Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev
    );
    const valueStickIndex = activeRange.findIndex((stickValue) => stickValue === closestStickValue);

    return {
        valueRangeIndex,
        valueStickIndex,
    };
};

interface RangeChartProps {
    colorTheme: "red" | "blue";
    value: number;
    ranges: Range[];
    labelSuffix?: string;
    animationDelay?: number;
}
export const RangeChart = ({
    colorTheme,
    value,
    ranges,
    labelSuffix = "",
    animationDelay,
}: RangeChartProps) => {
    const [left, setLeft] = useState(0);
    const [width, setWidth] = useState(MIN_CONTAINER_WIDTH);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const targetRef = useRef<HTMLDivElement | null>(null);

    const sticksRanges = useMemo(
        () => getSticksRanges(Object.values(ranges), width),
        [width, ranges]
    );
    const { valueRangeIndex, valueStickIndex } = useMemo(
        () => getValueStickPosition(value, ranges, sticksRanges),
        [value, ranges, sticksRanges]
    );

    useLayoutEffect(() => {
        containerRef?.current && setWidth(containerRef.current.clientWidth);
        targetRef?.current && setLeft(targetRef.current.offsetLeft);
    }, [width]);

    const renderSticks = () => {
        return sticksRanges.map((range, i) => {
            return range.map((item, j) => (
                <div
                    key={item}
                    ref={(ref) => {
                        if (valueRangeIndex === i && valueStickIndex === j) {
                            targetRef.current = ref;
                        }
                    }}
                    className={classNames("h-6 w-[3px] rounded-sm", COLOR_THEMES[colorTheme][i])}
                />
            ));
        });
    };

    const renderLegend = () => {
        return ranges.map(({ id }, index) => (
            <span
                key={id}
                className={classNames({
                    "font-bold text-zing-gray-40": valueRangeIndex === index,
                    "font-semibold text-zing-gray-200": valueRangeIndex !== index,
                })}
            >
                {i18n.t(id)}
            </span>
        ));
    };

    return (
        <div className="grid w-full justify-items-center gap-7" ref={containerRef}>
            <div
                className={classNames(
                    "relative z-20 rounded-2xl px-6 py-1 text-zing-16-22 font-semibold",
                    COLOR_THEMES[colorTheme][valueRangeIndex]
                )}
            >
                <div className="relative z-20">
                    {i18n.t("you_bmi", { bmi: value })}
                    {labelSuffix}
                </div>
            </div>
            <div className="grid gap-4">
                <div className="relative flex items-center gap-0.5">
                    {renderSticks()}
                    <div
                        className="absolute h-10 w-[3px] bg-white transition-all duration-[2000ms] ease-in-out"
                        style={{ left: `${left}px`, transitionDelay: `${animationDelay}ms` }}
                    />
                </div>
                <div className="flex w-full justify-between text-zing-10-18 uppercase lg:text-zing-12-18">
                    {renderLegend()}
                </div>
            </div>
        </div>
    );
};
